import { useEffect, useState } from "react";
import { api, fmtMoney, CURRENCIES } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Banknote } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./OverviewTab";

const IOI_STATUSES = [
    { v: "active", label: "Active", color: "text-blue-700" },
    { v: "accepted", label: "Accepted", color: "text-green-700" },
    { v: "rejected", label: "Rejected", color: "text-red-700" },
    { v: "withdrawn", label: "Withdrawn", color: "text-muted-foreground" },
];

export default function IOIsTab({ dealId, deal }) {
    const [iois, setIois] = useState([]);
    const [tracked, setTracked] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const refresh = async () => {
        const [a, b] = await Promise.all([
            api.get(`/iois/deal/${dealId}`),
            api.get(`/deals/${dealId}/buyers`),
        ]);
        setIois(a.data);
        setTracked(b.data);
        setLoading(false);
    };
    useEffect(() => { refresh(); }, [dealId]);

    const updateStatus = async (id, status) => {
        await api.patch(`/iois/${id}`, { status });
        toast.success(`Marked ${status}`);
        refresh();
    };
    const del = async (id) => {
        if (!confirm("Delete this IOI?")) return;
        await api.delete(`/iois/${id}`);
        refresh();
    };

    const totalActive = iois.filter((i) => i.status === "active").reduce((s, i) => s + (i.value || 0), 0);
    const ccy = deal.currency;

    return (
        <div className="space-y-4 max-w-5xl" data-testid="iois-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {IOI_STATUSES.map((s) => {
                    const count = iois.filter((i) => i.status === s.v).length;
                    return (
                        <div key={s.v} className="border border-border rounded-md bg-white p-4">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
                            <div className={`font-mono text-2xl font-bold ${s.color}`}>{count}</div>
                        </div>
                    );
                })}
            </div>

            <div className="border border-border rounded-md bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-display font-bold tracking-tight flex items-center gap-2">
                            <Banknote className="w-4 h-4 text-gold" /> Indications of Interest
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Active IOI value: <span className="font-mono text-navy font-medium">{fmtMoney(totalActive, ccy)}</span>
                        </p>
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="record-ioi-btn">
                                <Plus className="w-4 h-4 mr-1.5" /> Record IOI
                            </Button>
                        </DialogTrigger>
                        <RecordIOIDialog dealId={dealId} deal={deal} tracked={tracked} onCreated={() => { setOpen(false); refresh(); }} />
                    </Dialog>
                </div>

                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : iois.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-md">
                        No IOIs received yet. Record one when a buyer makes an indication.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="iois-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="text-left py-2">Buyer</th>
                                <th className="text-right py-2">Value</th>
                                <th className="text-left py-2 pl-4">Received</th>
                                <th className="text-left py-2">Status</th>
                                <th className="text-left py-2">Notes</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {iois.map((i) => (
                                <tr key={i.id} className="border-b border-border/60" data-testid={`ioi-row-${i.id}`}>
                                    <td className="py-3">
                                        <div className="font-medium">{i.buyer?.name || "—"}</div>
                                        {i.buyer?.company && <div className="text-xs text-muted-foreground">{i.buyer.company}</div>}
                                    </td>
                                    <td className="py-3 text-right font-mono font-medium text-navy">{fmtMoney(i.value, i.currency)}</td>
                                    <td className="py-3 pl-4 text-xs font-mono text-muted-foreground">{new Date(i.received_at).toLocaleDateString()}</td>
                                    <td className="py-3">
                                        <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                                            <SelectTrigger className="h-8 w-32" data-testid={`ioi-status-${i.id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {IOI_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="py-3 text-xs text-muted-foreground max-w-xs truncate">{i.notes || "—"}</td>
                                    <td className="py-3 text-right">
                                        <Button size="icon" variant="ghost" onClick={() => del(i.id)} data-testid={`ioi-delete-${i.id}`}>
                                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function RecordIOIDialog({ dealId, deal, tracked, onCreated }) {
    const [form, setForm] = useState({
        buyer_id: "",
        value: "",
        currency: deal.currency || "GBP",
        received_at: new Date().toISOString().slice(0, 10),
        status: "active",
        notes: "",
    });
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);

    useEffect(() => {
        api.get("/contacts?type=buyer").then((r) => setContacts(r.data));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.buyer_id) return toast.error("Select a buyer");
        setLoading(true);
        try {
            await api.post(`/iois/deal/${dealId}`, {
                buyer_id: form.buyer_id,
                value: parseFloat(form.value) || 0,
                currency: form.currency,
                received_at: form.received_at ? new Date(form.received_at).toISOString() : null,
                status: form.status,
                notes: form.notes || null,
            });
            toast.success("IOI recorded");
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DialogContent data-testid="record-ioi-dialog">
            <DialogHeader>
                <DialogTitle className="font-display">Record an IOI</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <Field label="Buyer *">
                    <Select value={form.buyer_id} onValueChange={setF("buyer_id")}>
                        <SelectTrigger data-testid="ioi-buyer-select">
                            <SelectValue placeholder="Select a buyer…" />
                        </SelectTrigger>
                        <SelectContent>
                            {contacts.length === 0 ? (
                                <SelectItem value="none" disabled>No buyers in your contacts</SelectItem>
                            ) : contacts.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="IOI value *">
                        <Input type="number" required className="font-mono" value={form.value} onChange={setF("value")} data-testid="ioi-value-input" />
                    </Field>
                    <Field label="Currency">
                        <Select value={form.currency} onValueChange={setF("currency")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label="Status">
                        <Select value={form.status} onValueChange={setF("status")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{IOI_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                </div>
                <Field label="Date received">
                    <Input type="date" value={form.received_at} onChange={setF("received_at")} data-testid="ioi-date-input" />
                </Field>
                <Field label="Notes">
                    <Textarea rows={3} value={form.notes} onChange={setF("notes")} placeholder="Conditions, timeline, key terms…" />
                </Field>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="ioi-submit-btn">
                        {loading ? "Saving…" : "Record IOI"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
